import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import NoParameters from '../../../../../assets/images/device-management/no-params.png';
import { ocppMessageType } from '../../../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../../../shared-components/button/Button';
import CustomInput from '../../../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../../../shared-components/success-notification/SuccessNotification';
import ocppEvents from '../../../../../utils/api/ocpp-events';

import SetChargingProfile from './ocpp-commands/SetChargingProfile';

interface OcppCommandInputOption {
  label: string;
  value: string;
}

interface OcppCommandInput {
  label: string;
  value: string | number;
  key: string;
  valueRequired: boolean;
  type: 'number' | 'select-field' | string;
  options?: OcppCommandInputOption[];
  sendAsList?: boolean;
}

interface OcppCommandProperties {
  name: string;
  type: string;
  isActive: boolean;
  disableCommand: boolean;
  inputs: OcppCommandInput[];
}

const configurationOptions = [
  { label: 'Allow Offline Tx For Unknown Id', value: 'AllowOfflineTxForUnknownId' },
  { label: 'Authorization Cache Enabled', value: 'AuthorizationCacheEnabled' },
  { label: 'Authorize Remote Tx Requests', value: 'AuthorizeRemoteTxRequests' },
  { label: 'Blink Repeat', value: 'BlinkRepeat' },
  { label: 'Clock Aligned Data Interval', value: 'ClockAlignedDataInterval' },
  { label: 'Connection Time Out', value: 'ConnectionTimeOut' },
  { label: 'Get Configuration Max Keys', value: 'GetConfigurationMaxKeys' },
  { label: 'Heartbeat Interval', value: 'HeartbeatInterval' },
  { label: 'Local Authorize Offline', value: 'LocalAuthorizeOffline' },
  { label: 'Local Pre Authorize', value: 'LocalPreAuthorize' },
  { label: 'Max Energy On Invalid Id', value: 'MaxEnergyOnInvalidId' },
  { label: 'Meter Values Aligned Data', value: 'MeterValuesAlignedData' },
  { label: 'Meter Values Sampled Data', value: 'MeterValuesSampledData' },
  { label: 'Meter Value Sample Interval', value: 'MeterValueSampleInterval' },
  { label: 'Minimum Status Duration', value: 'MinimumStatusDuration' },
  { label: 'Reset Retries', value: 'ResetRetries' },
  { label: 'Connector Phase Rotation', value: 'ConnectorPhaseRotation' },
  { label: 'Stop Transaction On EV Side Disconnect', value: 'StopTransactionOnEVSideDisconnect' },
  { label: 'Stop Transaction On Invalid Id', value: 'StopTransactionOnInvalidId' },
  { label: 'Stop Txn Aligned Data', value: 'StopTxnAlignedData' },
  { label: 'Stop Txn Sampled Data', value: 'StopTxnSampledData' },
  { label: 'Transaction Message Attempts', value: 'TransactionMessageAttempts' },
  { label: 'Transaction Message Retry Interval', value: 'TransactionMessageRetryInterval' },
  { label: 'Unlock Connector On EV Side Disconnect', value: 'UnlockConnectorOnEVSideDisconnect' },
  { label: 'WebSocket Ping Interval', value: 'WebSocketPingInterval' },
  { label: 'Number Of Connectors', value: 'NumberOfConnectors' },
  { label: 'Meter Values Aligned Data Max Length', value: 'MeterValuesAlignedDataMaxLength' },
  { label: 'Meter Values Sampled Data Max Length', value: 'MeterValuesSampledDataMaxLength' },
  { label: 'Connector Phase Rotation Max Length', value: 'ConnectorPhaseRotationMaxLength' },
  { label: 'Stop Txn Aligned Data Max Length', value: 'StopTxnAlignedDataMaxLength' },
  { label: 'Stop Txn Sampled Data Max Length', value: 'StopTxnSampledDataMaxLength' },
  { label: 'Supported Feature Profiles', value: 'SupportedFeatureProfiles' },
  { label: 'Supported Feature Profiles Max Length', value: 'SupportedFeatureProfilesMaxLength' },
  { label: 'Supported File Transfer Protocols', value: 'SupportedFileTransferProtocols' },
  { label: 'Cpo Name', value: 'CpoName' },
  { label: 'Security Profile', value: 'SecurityProfile' },
  { label: 'Certificate Store Max Length', value: 'CertificateStoreMaxLength' },
  { label: 'Certificate Signed Max Chain Size', value: 'CertificateSignedMaxChainSize' },
  { label: 'Additional Root Certificate Check', value: 'AdditionalRootCertificateCheck' },
  { label: 'Reserve Connector Zero Supported', value: 'ReserveConnectorZeroSupported' },
  { label: 'Local Auth List Enabled', value: 'LocalAuthListEnabled' },
  { label: 'Local Auth List Max Length', value: 'LocalAuthListMaxLength' },
  { label: 'Send Local List Max Length', value: 'SendLocalListMaxLength' },
  { label: 'Charge Profile Max Stack Level', value: 'ChargeProfileMaxStackLevel' },
  {
    label: 'Charging Schedule Allowed Charging Rate Unit',
    value: 'ChargingScheduleAllowedChargingRateUnit',
  },
  { label: 'Charging Schedule Max Periods', value: 'ChargingScheduleMaxPeriods' },
  { label: 'Connector Switch 3 to 1 Phase Supported', value: 'ConnectorSwitch3to1PhaseSupported' },
  { label: 'Max Charging Profiles Installed', value: 'MaxChargingProfilesInstalled' },
  { label: 'Vendor Id', value: 'vendorId' },
  { label: 'Charge Point SN', value: 'chargePointSN' },
  { label: 'Custom Idle Fee After Stop', value: 'CustomIdleFeeAfterStop' },
  { label: 'Charging Parameters Enabled', value: 'ChargingParametersEnabled' },
  { label: 'Security Event Enable', value: 'SecurityEvtEnable' },
  { label: 'Websocket Fragment Enabled', value: 'WebsocketFragmentEnabled' },
  { label: 'Disable Light While Idle', value: 'DisableLightWhileIdle' },
  { label: 'Net Auto Switch Enabled', value: 'NetAutoSwitchEnabled' },
  { label: 'Free Mode Active', value: 'FreeModeActive' },
  { label: 'Free Mode Rfid', value: 'FreeModeRfid' },
];

const triggerOptions = [
  {
    label: 'Boot Notification',
    value: 'BootNotification',
  },
  {
    label: 'Diagnostics Status Notification',
    value: 'DiagnosticsStatusNotification',
  },
  {
    label: 'Firmware Status Notification',
    value: 'FirmwareStatusNotification',
  },
  {
    label: 'Heartbeat',
    value: 'Heartbeat',
  },
  {
    label: 'Meter Values',
    value: 'MeterValues',
  },
  {
    label: 'Status Notification',
    value: 'StatusNotification',
  },
];

interface OperationsProperties {
  onActionTriggered?: () => void;
}

/**
 * Ocpp operation (Commands)
 * @param {OperationsProperties} properties - The properties
 * @returns {any} The result
 */
export default function Operations({ onActionTriggered }: OperationsProperties = {}) {
  const { t } = useTranslation('devices');
  const dispatch = useAppDispatch();
  const { selectedChargingPoint } = useAppSelector((s) => s.device);
  const [searchParameters] = useSearchParams();
  const hideSidebar = searchParameters.get('hideSidebar') === 'true';
  const [formStatus, setFormStatus] = useState({
    buttonLoading: false,
  });

  const [modalView, setModalView] = useState({
    status: false,
    title: '',
    content: <div />,
  });

  const [activeCommand, setActiveCommands] = useState<OcppCommandProperties>();

  const [availableCommands, setAvailableCommands] = useState<OcppCommandProperties[]>([
    {
      name: t('changeAvailability'),
      type: ocppMessageType.CHANGE_AVAILABILITY,
      isActive: true,
      disableCommand: false,
      inputs: [
        {
          label: t('connectorID'),
          value: '',
          key: 'connectorId',
          valueRequired: true,
          type: 'number',
        },
        {
          label: t('availabilityType'),
          value: '',
          key: 'type',
          valueRequired: true,
          type: 'select-field',
          options: [
            {
              label: t('operative'),
              value: 'Operative',
            },
            {
              label: t('inoperative'),
              value: 'Inoperative',
            },
          ],
        },
      ],
    },
    {
      name: t('getConfiguration'),
      type: ocppMessageType.GET_CONFIGURATION,
      isActive: false,
      disableCommand: false,
      inputs: [
        {
          label: t('configurationKey'),
          value: '',
          key: 'keys',
          valueRequired: false,
          type: 'select-field',
          options: configurationOptions,
          sendAsList: true,
        },
      ],
    },
    {
      name: t('changeConfiguration'),
      type: ocppMessageType.CHANGE_CONFIGURATION,
      isActive: false,
      disableCommand: false,
      inputs: [
        {
          label: t('configurationKey'),
          value: '',
          key: 'key',
          valueRequired: true,
          type: 'select-field',
          options: configurationOptions,
        },
        {
          label: t('value'),
          value: '',
          key: 'value',
          valueRequired: true,
          type: 'text',
        },
      ],
    },
    {
      name: t('changeConfiguration(Custom)'),
      type: ocppMessageType.CHANGE_CONFIGURATION_CUSTOM,
      isActive: false,
      disableCommand: false,
      inputs: [
        {
          label: t('configurationKey'),
          value: '',
          key: 'key',
          valueRequired: true,
          type: 'text',
        },
        {
          label: t('inputType'),
          value: '',
          key: 'inputType',
          valueRequired: true,
          type: 'select-field',
          options: [
            {
              label: t('string'),
              value: 'string',
            },
            {
              label: t('boolean'),
              value: 'boolean',
            },
          ],
        },
        {
          label: t('value'),
          value: '',
          key: 'value',
          valueRequired: true,
          type: 'text',
        },
      ],
    },
    {
      name: t('clearCache'),
      type: ocppMessageType.CLEAR_CACHE,
      isActive: false,
      disableCommand: false,
      inputs: [],
    },
    {
      name: t('remoteStartTransaction'),
      type: ocppMessageType.RMT_START_TRANSACTION,
      isActive: false,
      disableCommand: false,
      inputs: [
        {
          label: t('connectorID'),
          value: '',
          key: 'connectorId',
          valueRequired: true,
          type: 'number',
        },
      ],
    },
    {
      name: t('remoteStopTransaction'),
      type: ocppMessageType.RMT_STOP_TRANSACTION,
      isActive: false,
      disableCommand: false,
      inputs: [
        {
          label: t('transactionID'),
          value: '',
          key: 'transactionId',
          valueRequired: true,
          type: 'number',
        },
      ],
    },
    {
      name: t('reset'),
      type: ocppMessageType.RESET_CP,
      isActive: false,
      disableCommand: false,
      inputs: [
        {
          label: t('resetType'),
          value: '',
          key: 'resetType',
          valueRequired: true,
          type: 'select-field',
          options: [
            {
              label: t('hard'),
              value: 'Hard',
            },
            {
              label: t('soft'),
              value: 'Soft',
            },
          ],
        },
      ],
    },
    {
      name: t('unlockConnector'),
      type: ocppMessageType.UNLOCK_CONNECTOR,
      isActive: false,
      disableCommand: false,
      inputs: [
        {
          label: t('connectorID'),
          value: '',
          key: 'connectorId',
          valueRequired: true,
          type: 'number',
        },
      ],
    },
    {
      name: t('triggerMessage'),
      type: ocppMessageType.TRIGGER_MESSAGE,
      isActive: false,
      disableCommand: false,
      inputs: [
        {
          label: t('triggerMessage'),
          value: '',
          key: 'requestedMessage',
          valueRequired: true,
          type: 'select-field',
          options: triggerOptions,
        },
        {
          label: t('connectorID'),
          value: '',
          key: 'connectorId',
          valueRequired: false,
          type: 'number',
        },
      ],
    },
    {
      name: t('updateFirmware'),
      type: ocppMessageType.UPDATE_FIRMWARE,
      isActive: false,
      disableCommand: false,
      inputs: [
        {
          label: t('locationDirectoryURI'),
          value: '',
          key: 'location',
          valueRequired: true,
          type: 'text',
        },
        {
          label: t('retries'),
          value: '',
          key: 'retries',
          valueRequired: false,
          type: 'number',
        },
        {
          label: t('retryInterval'),
          value: '',
          key: 'retryInterval',
          valueRequired: false,
          type: 'number',
        },
        {
          label: t('retrieveDateTimeUTC'),
          value: '',
          key: 'retrieveDate',
          valueRequired: true,
          type: 'text',
        },
      ],
    },
    {
      name: t('getDiagnostics'),
      type: ocppMessageType.GET_DIAGNOSTICS,
      isActive: false,
      disableCommand: false,
      inputs: [
        {
          label: t('locationDirectoryURI'),
          value: '',
          key: 'location',
          valueRequired: true,
          type: 'text',
        },
        {
          label: t('retries'),
          value: '',
          key: 'retries',
          valueRequired: false,
          type: 'number',
        },
        {
          label: t('retryInterval'),
          value: '',
          key: 'retryInterval',
          valueRequired: false,
          type: 'number',
        },
        {
          label: t('startDateTimeUTC'),
          value: '',
          key: 'startTime',
          valueRequired: false,
          type: 'text',
        },
        {
          label: t('stopDateTimeUTC'),
          value: '',
          key: 'stopTime',
          valueRequired: false,
          type: 'text',
        },
      ],
    },
    {
      name: t('getLocalListVersion'),
      type: ocppMessageType.GET_LOCAL_LIST_VERSION,
      isActive: false,
      disableCommand: false,
      inputs: [],
    },
    {
      name: t('setChargingProfile'),
      type: ocppMessageType.SET_CHARGING_PROFILE,
      isActive: false,
      disableCommand: false,
      // Inputs required, but handled in a separate component
      inputs: [],
    },
    {
      name: t('clearChargingProfile'),
      type: ocppMessageType.CLEAR_CHARGING_PROFILE,
      isActive: false,
      disableCommand: false,
      inputs: [
        {
          label: 'ID',
          value: '',
          key: 'id',
          valueRequired: false,
          type: 'number',
        },
        {
          label: 'Connector ID',
          value: '',
          key: 'connectorId',
          valueRequired: false,
          type: 'number',
        },
        {
          label: t('chargingProfilePurpose'),
          value: '',
          key: 'chargingProfilePurpose',
          valueRequired: false,
          type: 'select-field',
          options: [
            {
              label: t('chargePointMaxProfile'),
              value: ' ChargePointMaxProfile',
            },
            {
              label: t('txDefaultProfile'),
              value: ' TxDefaultProfile',
            },
            {
              label: t('txProfile'),
              value: ' TxProfile',
            },
          ],
        },
        {
          label: t('stackLevel'),
          value: '',
          key: 'stackLevel',
          valueRequired: false,
          type: 'number',
        },
      ],
    },
  ]);

  /**
   * Function to change the active menu
   * @param {any} key - The key
   */
  const handleChangeActiveMenu = (key: string) => {
    setAvailableCommands((previousCommands) =>
      previousCommands.map((command) => ({
        ...command,
        isActive: command.type === key,
      })),
    );
  };

  /**
   * Handle change value func
   * @param {any} key - The key
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleChangeValue = (key: string, name: string, value: string | number) => {
    setAvailableCommands((previousCommands) =>
      previousCommands.map((command) => {
        if (command.type === key) {
          const updatedInputs = command.inputs.map((input) => {
            if (input.key === name) {
              return {
                ...input,
                value,
              };
            }
            return input;
          });

          return {
            ...command,
            inputs: updatedInputs,
          };
        }
        return command;
      }),
    );
  };

  /**
   * Function to handle api call for submitting the command
   * @param {string} type - The type
   * @param {any} requestBody - The requestBody
   */
  const handleApiCall = async (type: string, requestBody: any) => {
    // Just to make sure button is loading if this function called from other components
    setFormStatus({
      buttonLoading: true,
    });

    try {
      await ocppEvents.anyCommand({
        chargingPointSns: [selectedChargingPoint?.chargingPointSn],
        type,
        body: Object.fromEntries(
          Object.entries(requestBody).filter(([, value]) => value !== null && value !== ''),
        ),
      });

      if (onActionTriggered) {
        onActionTriggered();
      }

      setModalView({
        status: true,
        title: '',
        content: (
          <SuccessNotification
            title="Command submitted successfully"
            description="Your command has been submitted successfully."
            notificationCloseTimer={3000}
            onNotificationClose={() => setModalView({ status: false, title: '', content: <div /> })}
          />
        ),
      });
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: error?.response?.data?.message || 'Something went wrong!',
          type: 'error',
          code: 0,
        }),
      );
    } finally {
      setFormStatus({
        buttonLoading: false,
      });
    }
  };

  /**
   * Handle submit command
   */
  const handleSubmitCommand = async () => {
    setFormStatus({
      buttonLoading: true,
    });
    const currentActiveCommand = availableCommands.find((command) => command.isActive);
    if (!currentActiveCommand) {
      setFormStatus({
        buttonLoading: false,
      });
      return;
    }

    const requestBody: any = currentActiveCommand.inputs.reduce(
      (accumulator, item) => {
        if (item.type === 'number') {
          accumulator[item.key] = item.value === '' ? '' : Number(item.value);
        } else if (item.sendAsList) {
          accumulator[item.key] = item.value ? [item.value] : [];
        } else {
          accumulator[item.key] = item.value;
        }
        return accumulator;
      },
      {} as { [key: string]: string | number | (string | number)[] },
    );

    if (currentActiveCommand.type === ocppMessageType.CHANGE_CONFIGURATION_CUSTOM) {
      const finalRequestBody: any = {
        key: requestBody.key,
      };

      // By default string
      finalRequestBody.value = requestBody.value.toString();

      if (requestBody.inputType === 'boolean') {
        finalRequestBody.value = Boolean(requestBody.value);
      }

      await handleApiCall(ocppMessageType.CHANGE_CONFIGURATION, finalRequestBody);
      return;
    }

    await handleApiCall(currentActiveCommand.type, requestBody);
  };

  useEffect(() => {
    const foundActiveCommand: OcppCommandProperties | undefined = availableCommands.find(
      (command) => command.isActive && !command.disableCommand,
    );
    if (foundActiveCommand) {
      setActiveCommands(foundActiveCommand);
    }
  }, [availableCommands]);

  useEffect(() => {
    const operation = searchParameters.get('operation');
    const resetType = searchParameters.get('resetType');
    if (operation) {
      setAvailableCommands((previousCommands) =>
        previousCommands.map((command) => {
          const isActive = command.type === operation;
          if (isActive && resetType && command.type === ocppMessageType.RESET_CP) {
            const updatedInputs = command.inputs.map((input) => {
              if (input.key === 'resetType') {
                return {
                  ...input,
                  value: resetType,
                };
              }
              return input;
            });
            return {
              ...command,
              isActive,
              inputs: updatedInputs,
            };
          }
          return {
            ...command,
            isActive,
          };
        }),
      );
    }
  }, [searchParameters]);

  return (
    <div className="device-ocpp-operations-wrap">
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setModalView({ ...modalView, status: false })}
        header={false}
        rightCorner=""
        notification
      />
      {/* Tags if required */}
      <div />
      <div className={`device-ocpp-operations-container ${hideSidebar ? 'hide-sidebar' : ''}`}>
        {!hideSidebar && (
          <div className="ocpp-operations-sidebar-wrap">
            {availableCommands
              .filter((command) => !command.disableCommand)
              .map((command) => (
                <div
                  className={`sidebar-each-command-wrap ${command.isActive ? 'active-command' : ''}`}
                  role="presentation"
                  onClick={() => handleChangeActiveMenu(command.type)}>
                  <h6>{command.name}</h6>
                </div>
              ))}
          </div>
        )}
        {activeCommand?.type === ocppMessageType.SET_CHARGING_PROFILE ? (
          <div className="ocpp-operation-content-wrap">
            <SetChargingProfile
              handleApiCall={handleApiCall}
              buttonLoading={formStatus.buttonLoading}
            />
          </div>
        ) : (
          <div className="ocpp-operations-content-wrap">
            <div className="ocpp-operations-content-container">
              {(() => {
                if (activeCommand?.inputs?.length === 0) {
                  return <img src={NoParameters} alt="" className="no-params-required-image" />;
                }
                const contentToDisplay = activeCommand?.inputs.map((eachInput) => {
                  let inputDisplay;
                  switch (eachInput.type) {
                    case 'number':
                    case 'text':
                      inputDisplay = (
                        <CustomInput
                          name={eachInput.key}
                          inputLabel={eachInput.label}
                          type={eachInput.type}
                          placeholder={eachInput.label}
                          value={eachInput.value}
                          onChangeValue={(name: string, value: string | number) =>
                            handleChangeValue(activeCommand.type, name, value)
                          }
                          valueRequired={eachInput.valueRequired}
                          fontSize="small"
                        />
                      );
                      break;
                    case 'select-field':
                      inputDisplay = (
                        <SelectField
                          name={eachInput.key}
                          inputLabel={eachInput.label}
                          selectedValue={eachInput.value}
                          placeholder={eachInput.label}
                          onChangeValue={(name: string, value: string | number) =>
                            handleChangeValue(activeCommand.type, name, value)
                          }
                          valueRequired={eachInput.valueRequired}
                          options={eachInput.options || []}
                          fontSize="small"
                        />
                      );
                      break;
                    default:
                      inputDisplay = null;
                      break;
                  }
                  return inputDisplay;
                });
                return <div className="ocpp-operations-basic-commands">{contentToDisplay}</div>;
              })()}
            </div>
            <div className="data-management-form-buttons">
              <MyButton
                label={t('submit')}
                buttonType="submit"
                loading={formStatus.buttonLoading}
                disableButton={formStatus.buttonLoading}
                onClickFunc={handleSubmitCommand}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

Operations.defaultProps = {
  /**
   * Callback when action is triggered
   */
  onActionTriggered: () => {},
};
